import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Search, Mail, UserPlus, Building2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "content_manager", label: "Content Manager" },
  { value: "sr_sme", label: "Sr. SME" },
  { value: "associate_sme", label: "Associate SME" },
  { value: "bde", label: "BDE (Sales)" }
];

const formatRoleName = (role) => {
  const roleMap = {
    admin: "Admin",
    content_manager: "Content Manager",
    associate_sme: "Associate SME",
    sr_sme: "Sr. SME",
    bde: "BDE"
  };
  return roleMap[role] || role;
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("users");
  const [searchQuery, setSearchQuery] = useState("");
  
  // User modal
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "associate_sme",
    contact: ""
  });
  
  // Client modal
  const [showClientModal, setShowClientModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientForm, setClientForm] = useState({
    name: "",
    email: "",
    contact: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, clientsRes, onlineRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/attendance/online`)
      ]);
      setUsers(usersRes.data);
      setClients(clientsRes.data);
      setOnlineUsers(onlineRes.data.map(u => u.id));
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const isUserOnline = (userId) => onlineUsers.includes(userId);

  // User handlers
  const handleUserSubmit = async (e) => {
    e.preventDefault();
    if (!userForm.name || !userForm.email) {
      toast.error("Name and email are required");
      return;
    }
    if (!editingUser && !userForm.password) {
      toast.error("Password is required for new users");
      return;
    }

    try {
      if (editingUser) {
        await axios.put(`${API}/users/${editingUser.id}`, {
          name: userForm.name,
          email: userForm.email,
          role: userForm.role,
          contact: userForm.contact
        });
        toast.success("User updated");
      } else {
        await axios.post(`${API}/auth/register`, userForm);
        toast.success("User created successfully");
      }
      setShowUserModal(false);
      resetUserForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save user");
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      name: user.name,
      email: user.email,
      password: "",
      role: user.role,
      contact: user.contact || ""
    });
    setShowUserModal(true);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success("User deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const resetUserForm = () => {
    setEditingUser(null);
    setUserForm({ name: "", email: "", password: "", role: "associate_sme", contact: "" });
  };

  // Client handlers
  const handleClientSubmit = async (e) => {
    e.preventDefault();
    if (!clientForm.name) {
      toast.error("Client name is required");
      return;
    }

    try {
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, clientForm);
        toast.success("Client updated");
      } else {
        await axios.post(`${API}/clients`, clientForm);
        toast.success("Client created");
      }
      setShowClientModal(false);
      resetClientForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to save client");
    }
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setClientForm({
      name: client.name,
      email: client.email || "",
      contact: client.contact || ""
    });
    setShowClientModal(true);
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm("Delete this client? All orders will also be deleted.")) return;
    try {
      await axios.delete(`${API}/clients/${clientId}`);
      toast.success("Client deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete client");
    }
  };

  const resetClientForm = () => {
    setEditingClient(null);
    setClientForm({ name: "", email: "", contact: "" });
  };

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredClients = clients.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="users" className="gap-2">
            <UserPlus size={16} /> Users
          </TabsTrigger>
          <TabsTrigger value="clients" className="gap-2">
            <Building2 size={16} /> Clients
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="relative max-w-md flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-field pl-10"
              />
            </div>
            <button
              onClick={() => { resetUserForm(); setShowUserModal(true); }}
              className="btn-primary"
              data-testid="add-user-btn"
            >
              <Plus size={16} /> Add User
            </button>
          </div>

          <div className="data-table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Contact</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id} data-testid={`user-row-${user.id}`}>
                    <td>
                      <div className="flex items-center gap-2">
                        {isUserOnline(user.id) && (
                          <div className="online-indicator" title="Online"></div>
                        )}
                        <span className="font-medium">{user.name}</span>
                      </div>
                    </td>
                    <td className="text-gray-600">{user.email}</td>
                    <td>
                      <span className={`badge ${
                        user.role === "admin" ? "badge-danger" :
                        user.role === "content_manager" ? "badge-info" : 
                        user.role === "bde" ? "badge-warning" : "badge-neutral"
                      }`}>
                        {formatRoleName(user.role)}
                      </span>
                    </td>
                    <td className="text-gray-600">{user.contact || "-"}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        {isUserOnline(user.id) ? (
                          <span className="badge badge-success flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                            Online
                          </span>
                        ) : (
                          <span className={`badge ${user.is_active !== false ? "badge-neutral" : "badge-warning"}`}>
                            {user.is_active !== false ? "Offline" : "Inactive"}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleEditUser(user)}
                          className="btn-ghost text-blue-600"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="btn-danger"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Clients Tab */}
        <TabsContent value="clients" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="relative max-w-md flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search clients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-field pl-10"
              />
            </div>
            <button
              onClick={() => { resetClientForm(); setShowClientModal(true); }}
              className="btn-primary"
              data-testid="add-client-btn"
            >
              <Plus size={16} /> Add Client
            </button>
          </div>

          <div className="data-table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Contact</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((client) => (
                  <tr key={client.id} data-testid={`client-row-${client.id}`}>
                    <td className="font-medium">{client.name}</td>
                    <td className="text-gray-600">{client.email || "-"}</td>
                    <td className="text-gray-600">{client.contact || "-"}</td>
                    <td>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleEditClient(client)}
                          className="btn-ghost text-blue-600"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteClient(client.id)}
                          className="btn-danger"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* User Modal */}
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent data-testid="user-modal">
          <DialogHeader>
            <DialogTitle>{editingUser ? "Edit User" : "Add New User"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUserSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input
                value={userForm.name}
                onChange={(e) => setUserForm({...userForm, name: e.target.value})}
                placeholder="John Doe"
                data-testid="user-form-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                placeholder="john@prepify.com"
                data-testid="user-form-email"
              />
            </div>
            {!editingUser && (
              <div className="space-y-2">
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                  placeholder="Enter password"
                  data-testid="user-form-password"
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={userForm.role} onValueChange={(v) => setUserForm({...userForm, role: v})}>
                  <SelectTrigger data-testid="user-form-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Contact</Label>
                <Input
                  value={userForm.contact}
                  onChange={(e) => setUserForm({...userForm, contact: e.target.value})}
                  placeholder="Phone number"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowUserModal(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-user-btn">
                {editingUser ? "Update" : "Create User"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Client Modal */}
      <Dialog open={showClientModal} onOpenChange={setShowClientModal}>
        <DialogContent data-testid="client-modal">
          <DialogHeader>
            <DialogTitle>{editingClient ? "Edit Client" : "Add New Client"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleClientSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Client Name *</Label>
              <Input
                value={clientForm.name}
                onChange={(e) => setClientForm({...clientForm, name: e.target.value})}
                placeholder="Client Company Name"
                data-testid="client-form-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={clientForm.email}
                onChange={(e) => setClientForm({...clientForm, email: e.target.value})}
                placeholder="client@company.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Contact</Label>
              <Input
                value={clientForm.contact}
                onChange={(e) => setClientForm({...clientForm, contact: e.target.value})}
                placeholder="Phone number"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowClientModal(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-client-btn">
                {editingClient ? "Update" : "Create Client"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
